<template>
  <div class="app-container">
    <!-- Only show header and sidebar when authenticated -->
    <template v-if="isAuthenticated">
      <!-- Full width header -->
      <header class="app-header">
        <div class="header-content">
          <div class="header-left">
            <h1 class="app-title">Vision Cloud</h1>
          </div>
          <div class="header-actions">
            <div class="user-menu">
              <div class="user-info" @click="toggleUserMenu">
                <span class="username">{{ userName }}</span>
                <span class="customer-name">({{ customerName }})</span>
                <i class="fas fa-chevron-down" :class="{ 'rotate': showUserMenu }"></i>
              </div>
              <div v-if="showUserMenu" class="user-dropdown">
                <button @click="logout" class="dropdown-item">
                  <i class="fas fa-sign-out-alt"></i> Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- Main content area with sidebar -->
      <div class="main-content">
        <!-- Sidebar -->
        <nav class="sidebar">
          <router-link to="/order-new-part" class="nav-link" active-class="active">
            <i class="fas fa-plus"></i> Order New Part
          </router-link>
          <router-link to="/weborders" class="nav-link" active-class="active">
            <i class="fas fa-shopping-cart"></i> Web Orders
          </router-link>
          <router-link to="/kits" class="nav-link" active-class="active">
            <i class="fas fa-box"></i> Kits
          </router-link>
          <router-link
            v-if="userRole === 'Admin'"
            to="/job-status"
            class="nav-link"
            active-class="active"
          >
            <i class="fas fa-clipboard-list"></i> Job Status
          </router-link>
          <router-link 
            v-if="userRole === 'Admin'"
            to="/user-management" 
            class="nav-link" 
            active-class="active"
          >
            <i class="fas fa-users-cog"></i> User Management
          </router-link>
        </nav>

        <!-- Page content -->
        <main class="page-content">
          <router-view />
        </main>
      </div>
    </template>

    <!-- Show only router-view for unauthenticated pages (login, register) -->
    <template v-else>
      <router-view />
    </template>
    
    <!-- Version display -->
    <VersionDisplay />
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import VersionDisplay from './components/VersionDisplay.vue';
import axios from 'axios';

export default {
  name: 'App',
  components: {
    VersionDisplay
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isAuthenticated = ref(false);
    const userName = ref('');
    const customerName = ref('');
    const userRole = ref('');
    const showUserMenu = ref(false);

    const checkAuth = async () => {
      // Don't check auth for login, register, and reset password routes
      if (route.path === '/login' || route.path === '/register' || route.path === '/reset-password') {
        isAuthenticated.value = false;
        return;
      }

      try {
        const response = await axios.get('/auth/check-auth');
        const user = response.data.user;
        userName.value = user.email;
        customerName.value = user.customer;
        userRole.value = user.role;
        isAuthenticated.value = true;
      } catch (error) {
        console.error('Error checking auth:', error);
        isAuthenticated.value = false;
        if (route.path !== '/login' && route.path !== '/register') {
          router.push('/login');
        }
      }
    };

    // Watch for route changes
    watch(() => route.path, () => {
      checkAuth();
    });

    const logout = async () => {
      try {
        await axios.post('/auth/logout');
      } catch (error) {
        console.error('Error during logout:', error);
      } finally {
        isAuthenticated.value = false;
        userName.value = '';
        customerName.value = '';
        userRole.value = '';
        showUserMenu.value = false;
        router.push('/login');
      }
    };

    const toggleUserMenu = () => {
      showUserMenu.value = !showUserMenu.value;
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const userMenu = document.querySelector('.user-menu');
      if (userMenu && !userMenu.contains(event.target)) {
        showUserMenu.value = false;
      }
    };

    onMounted(() => {
      checkAuth();
      document.addEventListener('click', handleClickOutside);
    });

    return {
      isAuthenticated,
      userName,
      customerName,
      userRole,
      logout,
      showUserMenu,
      toggleUserMenu
    };
  }
};
</script>

<style>
:root {
  --primary-color: #4f46e5;
  --primary-hover: #4338ca;
  --background: #ffffff;
  --background-alt: #f3f4f6;
  --text-primary: #111827;
  --text-secondary: #6b7280;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: var(--background);
  color: var(--text-primary);
}

.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.app-header {
  background-color: var(--primary-color);
  color: white;
  padding: 1rem 2rem;
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
}

.app-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.user-menu {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
}

.user-info:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.username {
  font-size: 1rem;
  font-weight: 500;
}

.customer-name {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 400;
  opacity: 0.9;
}

.fa-chevron-down {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.fa-chevron-down.rotate {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  margin-top: 0.5rem;
  z-index: 1000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  color: var(--text-primary);
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.dropdown-item:hover {
  background-color: var(--background-alt);
}

.dropdown-item i {
  color: var(--text-secondary);
}

.main-content {
  display: flex;
  flex: 1;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: 0;
  gap: 0.5rem;
}

.sidebar {
  width: 140px;
  background-color: var(--background);
  padding: 0.5rem 0.25rem;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  color: var(--text-primary);
  text-decoration: none;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
}

.nav-link:hover {
  background-color: var(--background-alt);
}

.nav-link.active {
  background-color: var(--primary-color);
  color: white;
}

.nav-link i {
  width: 14px;
  text-align: center;
  font-size: 0.875rem;
}

.page-content {
  flex: 1;
  padding: 1.25rem 1.25rem 1.25rem 0.75rem;
  background-color: var(--background);
}

@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
    padding: 0;
    gap: 0;
  }

  .sidebar {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    flex-direction: row;
    overflow-x: auto;
    padding: 0.5rem;
    gap: 0.5rem;
  }

  .nav-link {
    white-space: nowrap;
    padding: 0.5rem;
  }

  .user-dropdown {
    right: 1rem;
  }
}

/* Add styles for unauthenticated layout */
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* When not authenticated, make the router-view take full height */
.app-container:not(.authenticated) {
  height: 100vh;
}

.app-container:not(.authenticated) > router-view {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
